import { IFilterPayload } from '@/interfaces/IFilterPayload';
import { IFilterProvider } from '@/interfaces/filter-providers/IFilterProvider';
import { ILocationFilterProvider } from '@/interfaces/filter-providers/ILocationFilterProvider';
import { FilterKey } from '@/models/FilterKey';

export class CountryFilterSelection {
	private readonly filterProvider: IFilterProvider;
	private readonly locationFilterProvider: ILocationFilterProvider;

	public constructor(
		filterProvider: IFilterProvider,
		locationFilterProvider: ILocationFilterProvider
	) {
		this.filterProvider = filterProvider;
		this.locationFilterProvider = locationFilterProvider;
	}

	public async process(countries: string[]): Promise<void> {
		const areasPayload: IFilterPayload[] = [];
		for (const country of countries) {
			const areas = await this.getSelectedAreasFor(country);
			areasPayload.push(...areas);
		}

		const countriesPayload = countries.map((country) => {
			return { key: FilterKey.COUNTRY, value: country } as IFilterPayload;
		});

		await this.processAll(countriesPayload, areasPayload);
	}

	private async processAll(countries: IFilterPayload[], areas: IFilterPayload[]): Promise<void> {
		await this.filterProvider.processFiltersSelection([...countries, ...areas]);
	}

	private getAreaFilterSelectionFor(country: string): string[] {
		const areasForCountry = this.locationFilterProvider.identifyAreasFor(country);
		return this.filterProvider.getFilterSelection(FilterKey.AREA).filter((val) => areasForCountry.includes(val));
	}

	private async getSelectedAreasFor(country: string): Promise<IFilterPayload[]> {
		const areaFilterSelection = this.getAreaFilterSelectionFor(country);

		const areasPayload: IFilterPayload[] = areaFilterSelection.map((val) => {
			return {
				key: FilterKey.AREA,
				value: val,
			} as IFilterPayload;
		});

		for(const area of areaFilterSelection) {
			const citiesToProcess = await this.getSelectedCitiesFor(area);
			areasPayload.push(...citiesToProcess);
		}

		return areasPayload;
	}

	private async getSelectedCitiesFor(area: string): Promise<IFilterPayload[]> {
		const selectedCities = this.filterProvider.getFilterSelection(FilterKey.CITY);
		const citiesToProcess: IFilterPayload[] = [];
		for (const cityId of selectedCities) {
			const city = await this.locationFilterProvider.retrieveCityInfo?.(cityId);
			if (city?.areaId === area) {
				citiesToProcess.push({
					key: FilterKey.CITY,
					value: cityId,
				});
			}
		}
		return citiesToProcess;
	}
}
